import { getViewRegion } from "@/app/viewRegion";
import { INTERNAL_SUPPLIER, type ApiError } from "@hisense/shared";
import type { TFunction } from "i18next";
import i18n from "@/i18n";
import { compressImage } from "./compressImage";
import { translateInternalSupplier } from "./internalSupplier";

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

const API_ERROR_KEY: Record<string, string> = {
 fx_same_currency:'fxConvert.errSameCurrency',fx_rate_missing:'fxConvert.errRateMissing',fx_rate_changed:'fxConvert.errRateChanged',fx_conversion_mismatch:'fxConvert.errMismatch',
 admin_only_requires_admin:'pi.adminOnlyAdminsOnly', attachment_unknown:'evidence.errUnknown',requirement_unknown:'evidence.errUnknown',attachment_taken:'evidence.errTaken',photo_taken:'evidence.errTaken',customer_code_taken:'customers.codeTaken',
 material_stock:'productionMaterials.stockError',material_plan:'productionMaterials.planError',material_source:'productionMaterials.sourceError',material_return_excess:'productionMaterials.returnError',material_assessment_excess:'productionMaterials.assessmentError',material_dependency:'productionMaterials.dependencyError',material_locked:'productionMaterials.locked',material_changed:'productionMaterials.changed',material_dates:'productionMaterials.datesError',material_invalid:'productionMaterials.invalid',material_correction:'productionMaterials.correctionError',
 execution_source:'execution.sourceError',execution_custody:'execution.custodyError',execution_quantity:'execution.quantityError',execution_progress:'execution.progressError',execution_inputs:'execution.inputsError',inspection_measurement:'execution.measurementError',inspection_duplicate:'execution.duplicateError',execution_operation:'execution.operationError',execution_dependency:'execution.dependencyError',execution_locked:'execution.locked',execution_changed:'execution.changed',execution_dates:'execution.datesError',execution_correction:'execution.correctionError',inspection_source:'execution.inspectionSource',inspection_excess:'execution.inspectionExcess',inspection_screening:'execution.inspectionScreening',inspection_changed:'execution.inspectionChanged',inspection_dependency:'execution.inspectionDependency',inspection_locked:'execution.inspectionLocked',inspection_incomplete:'execution.inspectionIncomplete',inspection_evidence:'execution.inspectionEvidence',inspection_deviation:'execution.inspectionDeviation',inspection_standard:'execution.inspectionStandard',production_reconcile:'execution.reconcileError',
 spec_locked:'qualitySpecs.locked',spec_changed:'qualitySpecs.changed',spec_source:'qualitySpecs.sourceError',
 production_invalid:'production.invalid',production_changed:'production.changed',production_locked:'production.locked',production_capacity:'production.capacityError',production_source:'production.sourceError',production_source_quantity:'production.quantityError',production_schedule:'production.scheduleError',production_dates:'production.dateError',production_timezone:'production.timezoneError',production_dependency:'production.dependency',
 sourcing_request_incomplete:'sourcing.requestIncomplete',sourcing_dates:'sourcing.datesError',sourcing_changed:'sourcing.changed',sourcing_locked:'sourcing.locked',sourcing_source:'sourcing.sourceError',sourcing_lines:'sourcing.invalid',sourcing_price:'sourcing.invalid',sourcing_overallocated:'sourcing.overallocated',sourcing_dependency:'sourcing.dependency',sourcing_incomplete:'sourcing.termsRequired',sourcing_single_source:'sourcing.singleSourceError',sourcing_expired:'sourcing.expiredError',sourcing_order_locked:'sourcing.orderLocked',sourcing_preview_required:'sourcing.orderLocked',
 description_required:'productEditor.descriptionRequired',type_required:'productEditor.typeRequired',
 sub_ref_required:'productEditor.subRefRequired',duplicate_identity:'productEditor.duplicateIdentity',sub_ref_required_on_other:'productEditor.subRefRequiredOnOther',
 stock_managed_by_lot:'productStock.managedByLot',stock_adjustment_required:'productStock.managedByLot',opening_import_invalid:'openingImport.fixFirst',opening_import_empty:'openingImport.empty',opening_import_reused:'openingImport.reused',
 stock_count_locked:'stockCounts.locked',stock_count_source:'stockCounts.sourceError',stock_count_variance:'stockCounts.varianceError',stock_count_reserved:'stockCounts.reservedError',stock_count_changed:'stockCounts.changed',stock_count_dependency:'stockCounts.dependencyError',
 subcontract_locked:'subcontract.locked',subcontract_source:'subcontract.sourceError',subcontract_stock:'subcontract.stockError',subcontract_custody:'subcontract.custodyError',subcontract_inputs:'subcontract.inputsError',subcontract_quantity:'subcontract.quantityError',subcontract_changed:'subcontract.changed',subcontract_dependency:'subcontract.dependency',subcontract_inspect:'subcontract.inspectError',
 ncr_locked:'ncr.locked',ncr_incomplete:'ncr.incomplete',ncr_source:'ncr.sourceError',ncr_changed:'ncr.changed',
 sales_issue_locked:'salesIssues.locked',sales_issue_source:'salesIssues.sourceError',sales_issue_stock:'salesIssues.stockError',sales_issue_return:'salesIssues.returnError',sales_issue_changed:'salesIssues.changed',sales_issue_dependency:'salesIssues.locked',sales_issue_inspect:'salesIssues.sourceError',
 portal_lockout:'portal.forbidden',
 ticket_locked:'tickets.error',ticket_source:'tickets.sourceError',ticket_changed:'tickets.error',
 ticket_claimed:'supplierClaims.errors.ticketClaimed',supplier_claim_locked:'supplierClaims.errors.locked',supplier_claim_self_approval:'supplierClaims.errors.selfApproval',supplier_claim_resend:'supplierClaims.errors.resend',supplier_claim_source:'supplierClaims.errors.source',supplier_claim_response:'supplierClaims.errors.response',supplier_claim_approval:'supplierClaims.errors.approval',supplier_claim_changed:'supplierClaims.errors.changed',
 claim_issue:'claims.errors.claim_issue',claim_type_not_allowed:'claims.errors.claim_type_not_allowed',claim_type_duplicate:'claims.errors.claim_type_duplicate',claim_amount:'claims.errors.claim_amount',claim_amount_not_monetary:'claims.errors.claim_amount_not_monetary',claim_record_only_exclusive:'claims.errors.claim_record_only_exclusive',claim_currency:'claims.errors.claim_currency',claim_severity:'claims.errors.claim_severity',
 cost_unmapped:'gl.unmapped',cost_locked:'gl.locked',cost_changed:'gl.changed',cost_source:'gl.sourceError',

 po_unit:'so.unitError',invoice_unit:'so.unitError',
mrp_open_production:'mrp.openProduction',mrp_supply_changed:'mrp.supplyChanged',mrp_source_changed:'mrp.sourceChanged',reservation_exceeds:'reservations.errors.reservation_exceeds',reservation_source:'reservations.errors.reservation_source',reservation_invalid:'reservations.errors.reservation_invalid',reservation_locked:'reservations.errors.reservation_locked',reservation_conflict:'reservations.errors.reservation_conflict',reservation_changed:'reservations.errors.reservation_changed',reservation_duplicate:'reservations.errors.reservation_duplicate',mrp_pr_precision:'mrp.prPrecision',mrp_pr_lines:'mrp.prLines',mrp_demand_claimed:'mrp.demandClaimed',mrp_dependency:'mrp.dependency',mrp_changed:'mrp.changed',mrp_run_locked:'mrp.runLocked',mrp_lot_constraint:'mrp.lotConstraint',mrp_source:'mrp.sourceError',mrp_bom:'mrp.bomError',mrp_bom_unit:'mrp.bomError',mrp_due_date:'mrp.dateError',mrp_limit:'mrp.limit',mrp_cycle:'mrp.cycle',mrp_quantity:'mrp.invalid',mrp_unit:'mrp.invalid',mrp_policy:'mrp.invalid',mrp_duplicate:'mrp.invalid',mrp_date:'mrp.invalid',
 unit_profile_invalid:'unitPricing.invalid',unit_profile_changed:'unitPricing.changed',unit_profile_locked:'unitPricing.locked',unit_profile_source:'unitPricing.source',unit_profile_incomplete:'unitPricing.incomplete',
 so_invalid:'so.invalid',so_hold:'so.holdError',so_planned_date:'so.plannedDateError',so_locked:'so.locked',so_changed:'so.changed',so_duplicate:'so.duplicate',so_source:'so.sourceInvalid',so_source_changed:'so.sourceChanged',so_source_locked:'so.sourceLocked',so_quantity:'so.quantityInvalid',so_dependency:'so.dependency',so_unit:'so.unitError',so_instalment:'so.instalmentError',so_credit:'so.creditError',
 gl_unmapped:'gl.unmapped',gl_period:'gl.periodError',gl_match:'gl.matchError',gl_paid:'gl.paidError',gl_locked:'gl.locked',gl_changed:'gl.changed',gl_source:'gl.sourceError',gl_unbalanced:'gl.unbalanced',gl_applied:'gl.appliedError',gl_apply:'gl.applyError',gl_claim_closed:'gl.claimClosedError',gl_payable_mismatch:'gl.payableMismatch',supplier_claim_debit_note:'supplierClaims.errors.debitNote',
 portal_scope:'portal.scopeError',portal_source:'portal.sourceError',portal_locked:'portal.forbidden',
 unknown_location:'bins.errors.unknownLocation',bin_duplicate:'bins.errors.duplicate',bin_in_use:'bins.errors.inUse',bin_code_invalid:'bins.errors.invalidCode',bin_warehouse_invalid:'bins.errors.warehouse',bin_not_found:'bins.errors.notFound',
 preview_locked:'preview.locked',preview_password_invalid:'preview.wrong',preview_throttled:'preview.throttled',preview_unavailable:'preview.unavailable',
 device_parse:'devices.parseError',device_timeout:'devices.timeoutError',device_hardware:'devices.hardwareError',device_calibration:'devices.calibrationError',device_locked:'devices.error',device_changed:'devices.error',
 pr_invalid:'requisitions.invalid',pr_self_approval:'requisitions.selfApproval',pr_incomplete:'requisitions.incomplete',pr_locked:'requisitions.locked',pr_changed:'requisitions.changed',pr_dependency:'requisitions.dependency',pr_source:'requisitions.sourceError',pr_unit:'requisitions.unitError',
 routing_locked:'routings.locked',routing_invalid:'routings.invalid',routing_changed:'routings.changed',routing_source:'routings.sourceInvalid',routing_supplier:'routings.supplierInvalid',routing_incomplete:'routings.incomplete',routing_dependency:'routings.dependency',
 bom_locked:'boms.locked',bom_invalid:'boms.invalid',bom_changed:'boms.changed',bom_unit:'boms.unitError',bom_child:'boms.childError',bom_cycle:'boms.cycle',bom_depth:'boms.depth',bom_dependency:'boms.dependency',bom_source:'boms.sourceError',
 

 transfer_locked:'transfers.locked',
 transfer_changed:'transfers.changed',
 transfer_invalid:'transfers.invalid',
 transfer_insufficient:'transfers.insufficient',
 transfer_ship_first:'transfers.shipFirst',transfer_dependency:'transfers.dependency',
 iqc_locked:"iqc.locked",
 iqc_changed:"iqc.changed",
 iqc_source_invalid:"iqc.sourceInvalid",
 iqc_overallocated:"iqc.overallocated",
 iqc_documents_required:"iqc.documentsRequired",
 iqc_basis_required:"iqc.basisRequired",
 iqc_deviation_required:"iqc.deviationRequired",
 iqc_dependency:"iqc.dependency",
 iqc_file_type:"iqc.fileType",
 iqc_arrival_invalid:"iqc.arrivalInvalid",
 iqc_reason_required:"iqc.reasonRequired",
 iqc_retry_conflict:"iqc.changed",

 receipt_reason_required:"warehouse.reasonRequired",
  receipt_source_invalid: "warehouse.sourceChanged",
  receipt_source_changed: "warehouse.sourceChanged",
  receipt_overallocated: "warehouse.overallocated",
  receipt_changed: "warehouse.changed",
  receipt_retry_conflict: "warehouse.retry",
  receipt_locked: "warehouse.locked",
  receipt_source_locked: "warehouse.sourceLocked",
  receipt_line_removal: "warehouse.lineRemoval",
  receipt_quantity_invalid: "warehouse.quantityError",

  settings_changed:"settingsWorkspace.settings_changed",
  company_changed:"settingsWorkspace.company_changed",
  settings_default_invalid:"settingsWorkspace.settings_default_invalid",
  settings_option_in_use:"settingsWorkspace.settings_option_in_use",
  settings_options_required:"settingsWorkspace.settings_options_required",
  settings_label_invalid:"settingsWorkspace.settings_label_invalid",
  company_default_required:"settingsWorkspace.company_default_required",

  current_password_incorrect:"userWorkspace.currentIncorrect",
  password_unchanged:"userWorkspace.passwordUnchanged",
  account_changed:"userWorkspace.accountChanged",
  password_change_required:"userWorkspace.requiredGeneric",
  last_admin:"users.lastAdminProtected",
  po_currency_prices_required:"poWorkspace.po_currency_prices_required",
  profit_range_invalid:"profitWorkspace.invalidRange",
  quotation_changed: "quotationWorkspace.quotation_changed",
  quotation_issued_locked: "quotationWorkspace.quotation_issued_locked",
  quotation_reprice_required: "quotationWorkspace.quotation_reprice_required",
  quotation_customer_invalid: "quotationWorkspace.quotation_customer_invalid",
  quotation_retry_conflict: "quotationWorkspace.quotation_retry_conflict",
  quotation_line_invalid: "quotationWorkspace.quotation_line_invalid",
  quotation_publish_required: "quotationWorkspace.quotation_publish_required",
  quotation_revision_invalid: "quotationWorkspace.quotation_revision_invalid",

  duplicate_customer_po: "pi.duplicatePo",
  pi_currency_locked: "piWorkspace.pi_currency_locked",
  pi_dependencies_locked: "piWorkspace.pi_dependencies_locked",
  pi_payment_retry_conflict: "piWorkspace.pi_payment_retry_conflict",
  pi_void_reason_required: "piWorkspace.pi_void_reason_required",
  pi_party_unavailable: "piWorkspace.pi_party_unavailable",
  invoice_archived: "invoiceWorkspace.invoice_archived",
  invoice_line_exceeds: "invoiceWorkspace.invoice_line_exceeds",
  invoice_no_lines: "invoiceWorkspace.invoice_no_lines",
  invoice_parent_archived: "invoiceWorkspace.invoice_parent_archived",
  invoice_source_changed: "invoiceWorkspace.invoice_source_changed",
  invoice_changed: "invoiceWorkspace.invoice_changed",
  invoice_delivery_locked: "invoiceWorkspace.invoice_delivery_locked",
  packing_source_invalid: "packingWorkspace.packing_source_invalid",
  packing_identity_locked: "packingWorkspace.packing_identity_locked",
  packing_weight_invalid: "packingWorkspace.packing_weight_invalid",
  packing_empty: "packingWorkspace.packing_empty",
  packing_delivery_locked: "packingWorkspace.packing_delivery_locked",
  packing_invoice_invalid: "packingWorkspace.packing_invoice_invalid",
  packing_retry_conflict: "packingWorkspace.packing_retry_conflict",
  packing_changed: "packingWorkspace.packing_changed",
  packing_carton_conflict: "packingWorkspace.packing_carton_conflict",
  packing_invoice_delivery_exists: "packingWorkspace.packing_invoice_delivery_exists",
  packing_lot_required:"packingWorkspace.packing_lot_required",
  delivery_quantity_invalid: "deliveryWorkspace.delivery_quantity_invalid",
  delivery_transition_invalid: "deliveryWorkspace.delivery_transition_invalid",
  delivery_completion_required: "deliveryWorkspace.delivery_completion_required",
  delivery_reason_required: "deliveryWorkspace.delivery_reason_required",
  delivery_cancel_invalid: "deliveryWorkspace.delivery_cancel_invalid",
  delivery_finalized: "deliveryWorkspace.delivery_finalized",
  delivery_correction_required: "deliveryWorkspace.delivery_correction_required",
  delivery_overallocated: "deliveryWorkspace.delivery_overallocated",
  delivery_no_remaining: "deliveryWorkspace.delivery_no_remaining",
  delivery_source_invalid: "deliveryWorkspace.delivery_source_invalid",
  delivery_source_changed: "deliveryWorkspace.delivery_source_changed",
  delivery_fulfillment_required: "deliveryWorkspace.delivery_fulfillment_required",
  delivery_direct_note_required: "deliveryWorkspace.delivery_direct_note_required",
  delivery_stock_position_required: "deliveryWorkspace.delivery_stock_position_required",
  delivery_stock_position: "deliveryWorkspace.delivery_stock_position",
  delivery_stock_product: "deliveryWorkspace.delivery_stock_product",
  delivery_stock_unit: "deliveryWorkspace.delivery_stock_unit",
  delivery_stock_lot: "deliveryWorkspace.delivery_stock_lot",
  delivery_stock_short: "deliveryWorkspace.delivery_stock_short",
  delivery_stock_locked: "deliveryWorkspace.delivery_stock_locked",
  delivery_stock_reserved: "deliveryWorkspace.delivery_stock_reserved",
  delivery_stock_source: "deliveryWorkspace.delivery_stock_source",
  delivery_stock_changed: "deliveryWorkspace.delivery_stock_changed",
  delivery_cancel_returned: "deliveryWorkspace.delivery_cancel_returned",
  delivery_stock_issued: "deliveryWorkspace.delivery_stock_issued",
  delivery_direct_review_invalid: "deliveryWorkspace.delivery_direct_review_invalid",
  delivery_changed: "deliveryWorkspace.delivery_changed",
  delivery_retry_conflict: "deliveryWorkspace.delivery_retry_conflict",
  delivery_lot_required: "deliveryWorkspace.delivery_lot_required",
  delivery_plan_locked: "deliveryWorkspace.delivery_plan_locked",
  delivery_receipt_state: "deliveryWorkspace.delivery_receipt_state",
  delivery_in_transit: "deliveryWorkspace.delivery_in_transit",
  delivery_legacy_links: "deliveryWorkspace.delivery_legacy_links",
  delivery_source_fulfilled: "deliveryWorkspace.delivery_source_fulfilled",
  opportunity_changed:"crm.changed",opportunity_locked:"crm.locked",opportunity_source:"crm.sourceError",wave_changed:"crm.waveChanged",wave_source_changed:"crm.waveSourceChanged",wave_quantity:"crm.waveQuantity",
  commercial_profile_changed:"commercialPricing.changed",
  commercial_reprice_required:"commercialPricing.reprice",
  unauthorized: "errors.unauthorized",
  forbidden: "errors.forbidden",
  validation_error: "errors.validation",
  not_found: "errors.notFound",
  email_taken: "errors.emailTaken",
  invoice_exists: "errors.invoiceExists",
  currency_mismatch: "errors.currencyMismatch",
  not_restorable: "errors.notRestorable",
  no_file: "errors.noFile",
  bad_type: "errors.badImageType",
  too_large: "errors.imageTooLarge",
  company_taken: "errors.companyTaken",
  supplier_company_taken: "errors.supplierCompanyTaken",
  portal_login_id_taken: "errors.portalLoginIdTaken",
  portal_login_id_format: "errors.portalLoginIdFormat",
  portal_account_exists: "errors.portalAccountExists",
  portal_account_missing: "errors.portalAccountMissing",
  original_row_missing: "errors.originalRowMissing",
  customer_required: "errors.customerRequired",
  customer_region: "errors.customerRegion",
  supplier_region: "errors.supplierRegion",
  follower_invalid: "errors.followerInvalid",
  follower_region: "errors.followerRegion",
  // Nhà cung cấp riêng tư (28/10/2026) — và khách riêng tư (30/09/2026) dùng chung mã private_admin_only.
  private_admin_only: "errors.privateAdminOnly",
  private_supplier: "errors.privateSupplier",
  admin_cannot_have_region: "userAccess.adminNoRegion",
  payment_company_required: "errors.paymentCompanyRequired",
  items_required: "errors.itemsRequired",
  product_code_exhausted: "errors.productCodeExhausted",
  pi_already_linked: "errors.piAlreadyLinked",
  supplier_mismatch: "errors.supplierMismatch",
  duplicate_ctn_no: "errors.duplicateCtnNo",
  over_packed: "errors.overPacked",
  unknown_line: "errors.unknownLine",
};

/**
 * Convert stable API error codes to localized, user-safe messages.
 *
 * NOTE this deliberately ignores `error.fields`. Where the API sends useful
 * detail (poService sends `{ number, po }` / `{ product }`), read
 * `err.fields` at the CALL SITE and interpolate the richer key there —
 * e.g. purchaseOrders.piAlreadyLinked — instead of this generic fallback.
 */
export function getLocalizedError(error: unknown, t: TFunction, fallbackKey: string): string {
  if (error instanceof ApiClientError) {
    const key = API_ERROR_KEY[error.code];
    // `fields` của máy chủ (ví dụ số dòng `line`) điền vào câu báo lỗi.
    if (key) return t(key, { ...(error.fields ?? {}) });
  }
  return t(fallbackKey);
}

type Query = Record<string, string | number | undefined>;

function qs(query?: Query): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

interface FetchOpts {
  method?: string;
  body?: unknown;
  /** Thân yêu cầu THÔ (Blob/ArrayBuffer) — cho từng phần của tệp lớn. */
  rawBody?: BodyInit;
  query?: Query;
  signal?: AbortSignal;
}

/** JSON + công ty đang xem (v1.8 mục 1.1). Không có gì để gửi thì không đặt header nào, như trước. */
function requestHeaders(json: boolean): Record<string, string> | undefined {
  const view = getViewRegion();
  const h: Record<string, string> = {};
  if (json) h["content-type"] = "application/json";
  if (view) h["x-data-region"] = view;
  return Object.keys(h).length ? h : undefined;
}

/** Single entry point to the API. Sends cookies, normalizes errors. */
export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const res = await fetch(`/api${path}${qs(opts.query)}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    // rawBody đi THẲNG, không bọc JSON: dùng cho từng phần của tệp lớn — bọc
    // base64 vào JSON sẽ phình 33% và phải giữ cả phần trong bộ nhớ.
    headers: requestHeaders(!!opts.body),
    body: opts.rawBody ?? (opts.body ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text().catch(() => "");
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  // Nhà cung cấp riêng tư (28/10/2026): dịch nhãn máy chủ đặt thay tên — chỉ khi phản hồi có nhãn đó.
  if (text.includes(INTERNAL_SUPPLIER)) data = translateInternalSupplier(data, i18n.t("common.internalSupplier"));
  if (!res.ok) {
    const err = (data as ApiError | null)?.error;
    // `/auth/me` là phép DÒ phiên lúc khởi động và `/auth/login` là lần thử
    // đăng nhập — 401 ở hai chỗ đó là câu trả lời bình thường, không phải phiên
    // vừa hết hạn. Chuyển hướng ở đó sẽ tải lại trang mỗi lần mở app khi chưa
    // đăng nhập, và xoá mất email vừa gõ khi sai mật khẩu.
    if (res.status === 401 && path !== "/auth/me" && path !== "/auth/login") onSessionExpired();
    // Hết hạn mở khoá tính năng đang làm: báo cổng ở trang hiện ra màn mật khẩu lại.
    if (err?.code === "preview_locked") window.dispatchEvent(new CustomEvent("feature-preview-locked", { detail: { path } }));
    throw new ApiClientError(
      res.status,
      err?.code ?? "error",
      err?.message ?? res.statusText,
      err?.fields,
    );
  }
  return data as T;
}

export interface UploadedFile {
  key: string;
  url: string;
  /** Server-classified from the MIME type — images and documents have different caps. */
  kind: "image" | "document";
  fileName: string;
  fileSize: number;
  mimeType: string;
}

/**
 * Tải tệp lên: trả về key đã lưu, URL qua Worker và thông tin tệp. `folder`
 * chọn tiền tố key trên R2; máy chủ vẫn có allowlist riêng của nó.
 *
 * ẢNH ĐƯỢC NÉN TRƯỚC (xem compressImage) — nén ở đây chứ không ở từng ô chọn
 * tệp, vì cả bốn ô đều đi qua hàm này nên không có đường nào lọt. Tệp không
 * phải ảnh đi qua nguyên vẹn.
 */
/**
 * Phiên hết hạn thì ĐƯA VỀ TRANG ĐĂNG NHẬP, giữ lại đường dẫn đang xem.
 *
 * Phiên cố định 8 giờ và không gia hạn, nên hết hạn giữa ca làm là chuyện xảy
 * ra hằng ngày. Trước đây mọi request chỉ trả 401 và mỗi màn tự hiện "không tải
 * được dữ liệu" — người dùng bấm Thử lại mãi mà không hiểu vì sao, trong khi
 * thứ họ cần là đăng nhập lại.
 *
 * Dùng `location.assign` chứ không phải router: hàm này nằm ở tầng fetch, không
 * có quyền truy cập vào router, và một lần tải lại sạch cũng xoá luôn cache
 * TanStack Query của phiên cũ.
 */
let redirecting = false;
function onSessionExpired() {
  if (redirecting) return; // nhiều request cùng hỏng chỉ được chuyển hướng MỘT lần
  if (window.location.pathname.startsWith("/login")) return;
  redirecting = true;
  const from = window.location.pathname + window.location.search;
  window.location.assign(`/login?from=${encodeURIComponent(from)}`);
}

/**
 * `original`: tải NGUYÊN BẢN, không nén / đổi định dạng — cho bằng chứng đặt hàng của khách (yêu cầu v1.8 mục 4.3): PO gốc và ảnh chụp
 * đơn qua chat phải lấy lại được đúng tệp khách gửi, và nén có thể làm nhoè chữ trong ảnh chụp màn hình.
 */
export async function uploadFile(file: File, folder?: string, opts: { original?: boolean } = {}): Promise<UploadedFile> {
  const fd = new FormData();
  fd.append("file", opts.original ? file : await compressImage(file));
  if (folder) fd.append("folder", folder);
  const res = await fetch("/api/files", { method: "POST", credentials: "include", body: fd });
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data as ApiError | null)?.error;

    throw new ApiClientError(res.status, err?.code ?? "error", err?.message ?? res.statusText);
  }
  return (data as { data: UploadedFile }).data;
}

/** Tải ảnh lên → key + URL qua Worker. Phần nén nằm trong uploadFile. */
export async function uploadImage(file: File): Promise<{ key: string; url: string }> {
  const { key, url } = await uploadFile(file);
  return { key, url };
}
